import { ReportRequest } from '@/client/reports';
import { Subjects } from '@/client/users';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { usePhishingSignReports } from '@/hooks/query/reports.hook';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { AccordionTabHeader } from '@/ui/accordion';
import {
  createCustomTooltip,
  getBackgroundColors,
  getClickedBackgroundColors,
  getClickedBorderColors,
  getReportedBackgroundColors,
  getReportedBorderColors,
  getSubmittedBackgroundColors,
  getSubmittedBorderColors,
  hideTooltipForNavigation,
} from '@/ui/charts/charts.helper';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getPhishingSignsChartData,
  getRadarChartTickCallback,
} from '@/utils/helpers/analytics.helper';
import {
  colorBlackMain,
  colorGrayDark,
  colorGrayDarker,
  colorGrayPale,
  colorGreenRadarDarkBorder,
  colorGreenRadarLightBorder,
  colorGreenRadarMiddleBorder,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ChartWrapper = styled(FlexContainer)`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 40%;
  width: 100%;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  background: var(--white-main);
  min-height: 100%;
  overflow: hidden;
`;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;

  canvas {
    max-width: 100% !important;
    width: 100% !important;
    height: 431px !important;
  }
`;

type RadarChartProps = {
  filters: ReportRequest;
};

export const RadarChart = ({ filters }: RadarChartProps) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const docStyle = getComputedStyle(document.documentElement);
  const zoomLevel = window.devicePixelRatio || 1;
  const account = useAppSelector(selectCurrentAccount);
  const hasReportingEnabled = canUseFeature(
    Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING,
  );
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [datasets, setDatasets] = useState<
    {
      label: string;
      value: {
        reportedRate: number | null;
        clickedRate: number | null;
        submittedRate: number | null;
        sentCount: number | null;
        background: number | null;
        noData: number | null;
      } | null;
    }[]
  >([]);
  const [datasetVisibility, setDatasetVisibility] = useState({
    reported: hasReportingEnabled,
    clicked: true,
    submitted: true,
    background: true,
  });

  const { phishingSigns, isLoading } = usePhishingSignReports(
    account?.id as string,
    filters,
  );

  const handleLegendClick = (datasetKey: string) => {
    setDatasetVisibility((prev) => ({
      ...prev,
      [datasetKey]: !prev[datasetKey as keyof typeof prev],
    }));
  };

  const hasNoData =
    !phishingSigns || phishingSigns.length === 0 || datasets.length === 0;

  useEffect(() => {
    if (!phishingSigns) return;

    const {
      labels,
      reportedRateData,
      clickedRateData,
      submittedRateData,
      backgroundData,
      noDataData,
      maxScale,
      datasets,
    } = getPhishingSignsChartData(t, phishingSigns);

    setDatasets(datasets || []);

    const textColor = colorBlackMain(docStyle);
    const chartDatasets = [
      {
        label: t('phishing.chart.clickRate'),
        backgroundColor: getClickedBackgroundColors(labels, docStyle),
        borderColor: getClickedBorderColors(labels, docStyle),
        hoverBackgroundColor: getClickedBackgroundColors(labels, docStyle),
        borderWidth: 1,
        fill: true,
        data: clickedRateData,
        circular: false,
        hidden: !datasetVisibility.clicked,
      },
      {
        label: t('phishing.chart.submissionRate'),
        backgroundColor: getSubmittedBackgroundColors(labels, docStyle),
        borderColor: getSubmittedBorderColors(labels, docStyle),
        hoverBackgroundColor: getSubmittedBackgroundColors(labels, docStyle),
        borderWidth: 1,
        fill: true,
        data: submittedRateData,
        circular: false,
        hidden: !datasetVisibility.submitted,
      },
      ...(hasReportingEnabled
        ? [
            {
              label: t('phishing.chart.reportRate'),
              backgroundColor: getReportedBackgroundColors(labels, docStyle),
              borderColor: getReportedBorderColors(labels, docStyle),
              hoverBackgroundColor: getReportedBackgroundColors(
                labels,
                docStyle,
              ),
              borderWidth: 1,
              fill: true,
              data: reportedRateData,
              circular: false,
              hidden: !datasetVisibility.reported,
            },
          ]
        : []),
      {
        label: t('phishing.analytics.signsWithSimulation'),
        backgroundColor: getBackgroundColors(labels, docStyle),
        borderWidth: 1,
        hoverBackgroundColor: getBackgroundColors(labels, docStyle),
        fill: true,
        data: backgroundData,
        circular: false,
        hidden: !datasetVisibility.background,
      },
      {
        label: 'No Data',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        fill: true,
        data: noDataData,
        circular: false,
      },
    ];

    const data = {
      labels: labels,
      datasets: chartDatasets,
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10,
            boxHeight: 10,
            padding: 37,
            font: {
              size: zoomLevel > 1.25 ? 10 : 12,
              family: platformFontFamily(docStyle),
            },
            generateLabels: () => {
              const baseLabels = [
                {
                  text: t('phishing.chart.clickRate'),
                  fillStyle: datasetVisibility.clicked
                    ? colorGreenRadarDarkBorder(docStyle)
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.clicked
                    ? colorGreenRadarDarkBorder(docStyle)
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.clicked,
                  datasetIndex: 0,
                },
                {
                  text: t('phishing.chart.submissionRate'),
                  fillStyle: datasetVisibility.submitted
                    ? colorGreenRadarMiddleBorder(docStyle)
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.submitted
                    ? colorGreenRadarMiddleBorder(docStyle)
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.submitted,
                  datasetIndex: 1,
                },
              ];

              const reportLabels = hasReportingEnabled
                ? [
                    {
                      text: t('phishing.chart.reportRate'),
                      fillStyle: datasetVisibility.reported
                        ? colorGreenRadarLightBorder(docStyle)
                        : colorGrayDark(docStyle),
                      strokeStyle: datasetVisibility.reported
                        ? colorGreenRadarLightBorder(docStyle)
                        : colorGrayDark(docStyle),
                      pointStyle: 'circle',
                      lineWidth: 1,
                      hidden: !datasetVisibility.reported,
                      datasetIndex: 2,
                    },
                  ]
                : [];

              const backgroundLabel = {
                text: t('phishing.analytics.signsWithSimulation'),
                fillStyle: datasetVisibility.background
                  ? colorGrayPale(docStyle)
                  : colorGrayDark(docStyle),
                strokeStyle: datasetVisibility.background
                  ? colorGrayPale(docStyle)
                  : colorGrayDark(docStyle),
                pointStyle: 'circle',
                lineWidth: 1,
                hidden: !datasetVisibility.background,
                datasetIndex: hasReportingEnabled ? 3 : 2,
              };

              return [...baseLabels, ...reportLabels, backgroundLabel];
            },
          },
          position: 'bottom',
          align: 'start',
          onClick: (e: any, legendItem: any) => {
            if (legendItem && legendItem.datasetIndex !== undefined) {
              const datasetKeys = hasReportingEnabled
                ? ['clicked', 'submitted', 'reported', 'background']
                : ['clicked', 'submitted', 'background'];
              const datasetKey = datasetKeys[legendItem.datasetIndex];
              if (datasetKey) {
                handleLegendClick(datasetKey);
              }
            }
          },
          onHover: (e: any, legendItem: any) => {
            const canvas = e.chart?.canvas;
            if (canvas) {
              canvas.style.cursor = legendItem?.text ? 'pointer' : 'default';
            }
          },
        },
        datalabels: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: (context: any) =>
            createCustomTooltip(context, t, datasets, true),
        },
      },
      scales: {
        r: {
          border: {
            dash: [5, 5],
          },
          grid: {
            color: colorGrayDark(docStyle),
            circular: false,
            drawOnChartArea: true,
            z: 1,
          },
          angleLines: {
            color: colorGrayDark(docStyle),
            display: true,
            borderDash: [5, 5],
            lineWidth: 1,
            z: 1,
          },
          beginAtZero: true,
          min: 0,
          max: maxScale,
          pointLabels: {
            display: true,
            color: colorBlackMain(docStyle),
            font: {
              size: zoomLevel > 1.25 ? 10 : 12,
              family: platformFontFamily(docStyle),
            },
            centerPointLabels: true,
            padding: zoomLevel > 1.25 ? 0 : 10,
          },
          ticks: {
            stepSize: 20,
            callback: getRadarChartTickCallback(maxScale || 100),
            font: {
              size: zoomLevel > 1.25 ? 12 : 16,
              family: platformFontFamily(docStyle),
            },
            color: colorGrayDarker(docStyle),
            backdropColor: 'transparent',
            showLabelBackdrop: false,
            z: 1,
          },
        },
      },
      interaction: {
        mode: 'point' as const,
        intersect: false,
      },
      stacked: true,
      onHover: (event: any) => {
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor = 'default';
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [phishingSigns, datasetVisibility]);

  useEffect(() => {
    return () => {
      const tooltipId = 'chart-tooltip-phishing-signs-chart';
      hideTooltipForNavigation(tooltipId);
    };
  }, []);

  return (
    <ChartWrapper>
      <AccordionTabHeader
        title={t('phishing.analytics.analysis.signs')}
        description={t('phishing.analytics.analysis.signs.description')}
        isSubHeader
      />
      <div className="w-full mt-4" style={{ flex: 1, display: 'flex' }}>
        {isLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : hasNoData ? (
          <ChartEmptyState
            minHeight={430}
            noBorder
            noMargin
            type={ChartEmptyStateType.PHISHING}
            title={t('phishing.analytics.analysis.signs.empty.title')}
            description={t(
              'phishing.analytics.analysis.signs.empty.description',
            )}
          />
        ) : (
          <ChartContainer>
            <Chart
              id="phishing-signs-chart"
              type="polarArea"
              data={chartData}
              options={chartOptions}
            />
          </ChartContainer>
        )}
      </div>
    </ChartWrapper>
  );
};
